/**
 * 动画导演 —— 把一步的 MoveRecord 翻译成一条可播放的时间轴。
 *
 * 核心机制（视觉棋盘回放）：
 *   逻辑层同步算完终局，但渲染不能直接画终局——否则动画没开始涉及的格子
 *   会提前显示「未来的棋子」，连锁级目标格会被过早遮蔽成长时间空洞。
 *   解法：play() 时深拷贝一份「交换前快照」作为视觉棋盘（visualBoard），
 *   每个动作在【启动时】（而非创建时）才推进视觉棋盘：
 *     交换启动 → 视觉棋盘换位；清除启动 → 格子变空；下落启动 → 源格清空、目标格落子并遮蔽。
 *   这样每一帧的画面都严格等于「时间轴此刻应有的盘面」。
 *
 * 铁律（历史教训，勿回退）：
 *   1. 禁止在【创建】动作时写 AnimState / hiddenTargets——play() 会一次性创建
 *      全部多级动作，创建时写入会让后续级的遮蔽/碎片提前出现（2026-10-15 空洞 bug）。
 *   2. 多个清除组并存时，state.shattering 必须【追加】而非整体覆盖——
 *      覆盖会让只有最后创建的一组碎片可见。
 *   3. 同一连锁级内同列多段下落必须先清空全部源格、再落全部目标格——
 *      否则顺序依赖会互相擦除。
 */

import { Timeline, Easing, createAction, instant, type Action } from '../anim/tween';
import type { MoveRecord, MoveLevelRecord } from '../core/game';
import type { Board0, Cell, Coord, PieceColor, SpecialType } from '../core/types';
import { MotionConfig } from './motion';

// ──── 动画时长常量（全局可调） ────

export const ANIM = {
  /** 交换：两颗棋子互换 */
  swapDuration: 130,
  /** 回弹：无效交换弹回 */
  bounceDuration: 180,
  /** 消除碎裂 */
  clearDuration: 190,
  /** 消除后到下落的短暂停顿（让玩家看清楚） */
  clearHoldPause: 45,
  /** 单格下落基础时长 */
  fallBase: 90,
  /** 每多落一格增加的时长 */
  fallPerCell: 38,
  /** 下落最短/最长 */
  fallMin: 130,
  fallMax: 320,
  /** 补料额外延迟（让它略晚于下落，形成层次） */
  spawnExtraDelay: 40,
  /** 特殊块弹入 */
  spawnSpecialDuration: 220,
  /** 连锁间的呼吸间隔 */
  chainGap: 30,
  /** 整体动画速度乘数（1 = 标准） */
  globalScale: 1,
} as const;

/** 下落时长按距离计算 */
export function fallDuration(distance: number): number {
  const d = Math.max(1, distance);
  const raw = ANIM.fallBase + (d - 1) * ANIM.fallPerCell;
  return Math.min(ANIM.fallMax, Math.max(ANIM.fallMin, raw)) * ANIM.globalScale * MotionConfig.speedScale;
}

export function scaled(ms: number): number {
  return ms * ANIM.globalScale * MotionConfig.speedScale;
}

// ──── 动画状态（渲染层读取） ────

/** 正在下落的一颗棋子（渲染时用浮点坐标） */
export interface FallingPiece {
  /** 当前浮点位置（可为分数，表示行与行之间） */
  x: number;
  y: number;
  color: string;
  /** 是否为新生成（用于控制透明度淡入） */
  spawned: boolean;
  alpha: number;
}

/** 正在碎裂的一颗棋子 */
export interface ShatteringPiece {
  cell: Coord;
  color: string;
  /** 0..1 碎裂进度 */
  progress: number;
}

/** 正在弹入的特殊块 */
export interface SpawningSpecial {
  cell: Coord;
  progress: number;
  special: SpecialType;
  color?: PieceColor;
}

export interface AnimState {
  /** 视觉棋盘：交换前快照，随动作推进。渲染层有它就不画逻辑棋盘 */
  visualBoard: Board0 | null;
  /** 交换中的两颗（含进度与双方颜色，供渲染层画滑动） */
  swap: {
    from: Coord; to: Coord; progress: number; reversed: boolean;
    aColor: PieceColor; bColor: PieceColor;
  } | null;
  /** 下落中的棋子集合 */
  falling: FallingPiece[];
  /** 碎裂中的棋子集合 */
  shattering: ShatteringPiece[];
  /** 正在弹入的特殊块 */
  spawning: SpawningSpecial[];
  /**
   * 本帧需要被静态层遮蔽的目标格。
   * 每帧由动作自己维护：下落启动时加入，完成时移除。
   */
  hiddenTargets: Set<string>;
}

// ──── 导演 ────

export interface DirectorHost {
  /** 将坐标换算为屏幕像素（含盘外行的虚拟坐标） */
  cellCenterXY(c: number, r: number): { x: number; y: number };
  /** 颜色代号 → 实际填色（供下落中的棋子用） */
  colorOf(key: string): string;
}

export interface PlayOptions {
  /** 交换前的棋盘快照（导演内部深拷贝） */
  preBoard: Board0;
  /** 终局棋盘引用（动作落格时读取目标内容） */
  finalBoard: Board0;
  onFinished(): void;
  /** 动画播放期间的每帧回调（用于触发重绘） */
  onFrame(): void;
}

function cloneBoard(b: Board0): Board0 {
  return { size: b.size, cells: b.cells.map((row) => row.map((c) => ({ ...c }))) };
}

export class AnimationDirector {
  state: AnimState = {
    visualBoard: null,
    swap: null,
    falling: [],
    shattering: [],
    spawning: [],
    hiddenTargets: new Set(),
  };

  private host: DirectorHost;
  private timeline: Timeline | null = null;
  private playing = false;
  private finalBoard: Board0 | null = null;
  private onFinishedCb: (() => void) | null = null;
  private onFrameCb: (() => void) | null = null;

  constructor(host: DirectorHost) {
    this.host = host;
  }

  get isPlaying(): boolean { return this.playing; }

  /** 当前是否有动画在播 */
  hasActiveVisuals(): boolean {
    return this.state.falling.length > 0
      || this.state.shattering.length > 0
      || this.state.spawning.length > 0
      || this.state.swap !== null;
  }

  /** 清空所有动画状态（重开时用） */
  reset(): void {
    this.timeline = null;
    this.playing = false;
    this.finalBoard = null;
    this.state.visualBoard = null;
    this.state.swap = null;
    this.state.falling = [];
    this.state.shattering = [];
    this.state.spawning = [];
    this.state.hiddenTargets = new Set();
    this.onFinishedCb = null;
    this.onFrameCb = null;
  }

  // ── 视觉棋盘操作 ──

  private vGet(c: Coord): Cell {
    const vb = this.state.visualBoard!;
    return vb.cells[c.r][c.c];
  }

  private vSet(c: Coord, cell: Cell): void {
    const vb = this.state.visualBoard!;
    vb.cells[c.r][c.c] = cell;
  }

  private vEmpty(c: Coord): void {
    this.vSet(c, { kind: 'EMPTY' });
  }

  private vSwap(a: Coord, b: Coord): void {
    const va = this.vGet(a);
    const vb = this.vGet(b);
    this.vSet(a, vb);
    this.vSet(b, va);
  }

  private vCellKey(c: Coord): string {
    return `${c.c},${c.r}`;
  }

  /** 播放一步的完整动画序列 */
  play(record: MoveRecord, opts: PlayOptions): void {
    this.reset();
    this.playing = true;
    this.onFinishedCb = opts.onFinished;
    this.onFrameCb = opts.onFrame;
    this.finalBoard = opts.finalBoard;
    this.state.visualBoard = cloneBoard(opts.preBoard);

    // 记录交换双方在交换前的颜色（供 drawSwapPair 画滑动）
    const aCell = this.vGet(record.from);
    const bCell = this.vGet(record.to);
    const aColor = aCell.color ?? 'neptune';
    const bColor = bCell.color ?? 'neptune';

    const tl = new Timeline();
    this.timeline = tl;

    // ── ① 交换 / 回弹
    if (record.swapped) {
      tl.then(this.makeSwapAction(record.from, record.to, aColor, bColor, false));
    } else {
      // 无效交换：先换过去，再弹回来（视觉棋盘同步换回）
      tl.then(this.makeSwapAction(record.from, record.to, aColor, bColor, false, scaled(ANIM.swapDuration * 0.7)));
      tl.then(this.makeSwapAction(record.from, record.to, aColor, bColor, true, scaled(ANIM.bounceDuration)));
      tl.then(instant(() => { this.onFinishedCb?.(); }));
      return;
    }

    // ── ② 主动引爆（若有）
    for (const d of record.detonations) {
      tl.then(this.makeClearAction(
        d.cells.map((c) => ({ cell: c.cell, color: this.host.colorOf(c.color) })),
        d.cells.map((c) => c.cell),
      ));
    }

    // ── ③ 逐级消除 + 下落
    for (const lvl of record.records) {
      this.appendLevel(tl, lvl);
    }

    // ── ④ 特殊块弹入（在对应级之后统一补播）
    if (record.spawns.length > 0) {
      tl.parallel(...record.spawns.map((s) => this.makeSpawnAction(s)));
    }

    tl.then(instant(() => { this.onFinishedCb?.(); }));
  }

  /** 把一个连锁级推入时间轴：先碎裂，再下落补料 */
  private appendLevel(tl: Timeline, lvl: MoveLevelRecord): void {
    // 碎裂（若有消除）
    if (lvl.cleared.length > 0) {
      const pieces = lvl.cleared.map((c) => ({
        cell: c.cell,
        color: this.host.colorOf(c.color),
      }));
      tl.then(this.makeClearAction(pieces, lvl.cleared.map((c) => c.cell)));
      tl.delay(scaled(ANIM.clearHoldPause));
    }

    // 下落 + 补料（同组共享一次棋盘推进：先清全部源格，再落全部目标格）
    if (lvl.falls.length > 0) {
      let groupAdvanced = false;
      const advanceGroup = () => {
        if (groupAdvanced) return;
        groupAdvanced = true;
        // 先清空全部源格（盘外来源跳过）
        for (const f of lvl.falls) {
          if (f.fromR >= 0) this.vEmpty({ c: f.fromC, r: f.fromR });
        }
        // 再落全部目标格（同列链式互不擦除）
        for (const f of lvl.falls) {
          const color = f.color ?? this.finalBoard?.cells[f.toR]?.[f.toC]?.color;
          const cell: Cell = f.special
            ? { kind: 'SPECIAL', color, special: f.special }
            : { kind: 'PIECE', color };
          this.vSet({ c: f.toC, r: f.toR }, cell);
          this.state.hiddenTargets.add(this.vCellKey({ c: f.toC, r: f.toR }));
        }
      };
      tl.parallel(...lvl.falls.map((f) => this.makeFallAction(f, advanceGroup)));
    }
  }

  // ──── 各类动作构造 ────

  /**
   * 交换动作：两颗棋子沿直线互换。
   * 启动时推进视觉棋盘并遮蔽两格；结束时解除遮蔽（静态层接管）。
   */
  private makeSwapAction(
    a: Coord, b: Coord,
    aColor: PieceColor, bColor: PieceColor,
    reversed: boolean,
    duration = scaled(ANIM.swapDuration),
  ): Action {
    let started = false;
    return createAction(duration, (t) => {
      if (!started) {
        started = true;
        // reversed（回弹）= 再换一次即换回原状
        this.vSwap(a, b);
        this.state.hiddenTargets.add(this.vCellKey(a));
        this.state.hiddenTargets.add(this.vCellKey(b));
      }
      const p = reversed ? 1 - t : t;
      this.state.swap = { from: a, to: b, progress: p, reversed, aColor, bColor };
      this.onFrameCb?.();
    }, Easing.easeOutCubic, () => {
      this.state.swap = null;
      this.state.hiddenTargets.delete(this.vCellKey(a));
      this.state.hiddenTargets.delete(this.vCellKey(b));
      this.onFrameCb?.();
    });
  }

  /**
   * 消除碎裂动作。
   * 启动时（而非创建时）：清空视觉格 + 追加碎片到 state.shattering。
   * 结束时按对象身份移除本批碎片。
   */
  private makeClearAction(
    pieces: { cell: Coord; color: string }[],
    clearedCells: Coord[],
  ): Action {
    let started = false;
    const local: ShatteringPiece[] = [];
    return createAction(scaled(ANIM.clearDuration), (t) => {
      if (!started) {
        started = true;
        for (const c of clearedCells) this.vEmpty(c);
        for (const p of pieces) {
          const s: ShatteringPiece = { cell: p.cell, color: p.color, progress: 0 };
          local.push(s);
          this.state.shattering.push(s); // 追加，不覆盖
        }
      }
      for (const s of local) s.progress = t;
      this.onFrameCb?.();
    }, Easing.easeInQuad, () => {
      // 按对象身份移除本批
      const ids = new Set(local);
      this.state.shattering = this.state.shattering.filter((s) => !ids.has(s));
      this.onFrameCb?.();
    });
  }

  /** 下落动作：从 from 滑到 to（组级棋盘推进由 advanceGroup 统一处理） */
  private makeFallAction(
    f: { fromC: number; fromR: number; toC: number; toR: number; spawned: boolean; color?: PieceColor; special?: SpecialType },
    advanceGroup: () => void,
  ): Action {
    const from = this.host.cellCenterXY(f.fromC, f.fromR);
    const to = this.host.cellCenterXY(f.toC, f.toR);
    const distance = Math.abs(f.toR - f.fromR);
    const color = this.host.colorOf(f.color ?? 'neptune');
    const duration = fallDuration(distance);
    const targetKey = `${f.toC},${f.toR}`;

    // 同列棋子同时出发（像一根柱子一起下落），不做错峰。
    let piece: FallingPiece | null = null;

    return createAction(
      duration,
      (tRaw) => {
        advanceGroup(); // 幂等：组内首个动作触发整组棋盘推进
        if (!piece) {
          piece = { x: from.x, y: from.y, color, spawned: f.spawned, alpha: f.spawned ? 0.35 : 1 };
          this.state.falling.push(piece);
        }
        piece.x = from.x + (to.x - from.x) * tRaw;
        piece.y = from.y + (to.y - from.y) * tRaw;
        // 补料棋子快速淡入（从盘外进入后即全不透明）
        piece.alpha = f.spawned ? Math.min(1, 0.35 + tRaw * 3) : 1;
        this.onFrameCb?.();
      },
      Easing.easeInQuad, // 重力加速度：越落越快
      () => {
        // 落定：释放目标格给静态层，移除叠层
        this.state.hiddenTargets.delete(targetKey);
        if (piece) {
          const idx = this.state.falling.indexOf(piece);
          if (idx >= 0) this.state.falling.splice(idx, 1);
        }
        this.onFrameCb?.();
      },
    );
  }

  /** 特殊块弹入：启动时落格并遮蔽（动画层画缩放弹入），结束时交还静态层 */
  private makeSpawnAction(s: { type: SpecialType; cell: Coord; color?: PieceColor }): Action {
    let started = false;
    const key = this.vCellKey(s.cell);
    const ref: SpawningSpecial = { cell: s.cell, progress: 0, special: s.type, color: s.color };
    return createAction(scaled(ANIM.spawnSpecialDuration), (t) => {
      if (!started) {
        started = true;
        // 视觉格落为终局内容（finalBoard 的该格即特殊块）
        const fc = this.finalBoard?.cells[s.cell.r]?.[s.cell.c];
        if (fc) this.vSet(s.cell, { ...fc });
        this.state.spawning.push(ref);
        this.state.hiddenTargets.add(key);
      }
      ref.progress = t;
      this.onFrameCb?.();
    }, Easing.easeOutBack, () => {
      const idx = this.state.spawning.indexOf(ref);
      if (idx >= 0) this.state.spawning.splice(idx, 1);
      this.state.hiddenTargets.delete(key);
      this.onFrameCb?.();
    });
  }

  /** 由渲染循环驱动（毫秒） */
  tick(dt: number): void {
    if (!this.timeline || !this.playing) return;
    this.timeline.tick(dt);
    if (this.timeline.isDone) {
      this.playing = false;
    }
  }

  /** 立即跳到结尾 */
  finish(): void {
    if (!this.timeline) return;
    this.timeline.finish();
    this.playing = false;
  }
}
